import { Button, Card, Space, Table, Tag } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '@/services/api.service';
import { useAuthStore } from '@/store/authStore';
import EducationPageChrome from './components/EducationPageChrome';

type Item = {
  id: string; code: string; stem_text: string; item_type: string; difficulty?: string | null;
  cognitive_level?: string | null; status: string; subject_name: string; grade_name?: string | null;
};

export default function QuestionItemsPage() {
  const { t } = useTranslation('education');
  const navigate = useNavigate();
  const permissions = useAuthStore((s) => s.user?.permissions || []);
  const canManage = permissions.includes('education.question.manage') || permissions.includes('education.admin');
  const query = useQuery({
    queryKey: ['education-question-items'],
    queryFn: async () => (await api.get<Item[]>('/education/questions')).data || [],
  });
  return (
    <EducationPageChrome
      title={t('questionItems')}
      extra={<Space>
        <Button onClick={() => navigate('/education/questions')}>{t('back')}</Button>
        {canManage && <Button type="primary" onClick={() => navigate('/education/questions/items/new')}>{t('newQuestionItem')}</Button>}
      </Space>}
    >
      <Card size="small">
        <Table rowKey="id" loading={query.isLoading} dataSource={query.data || []} columns={[
          { title: t('code'), dataIndex: 'code' },
          { title: t('questionStem'), dataIndex: 'stem_text', ellipsis: true },
          { title: t('subject'), dataIndex: 'subject_name' },
          { title: t('grade'), dataIndex: 'grade_name', render: (value) => value || '—' },
          { title: t('itemType'), dataIndex: 'item_type' },
          { title: t('difficulty'), dataIndex: 'difficulty', render: (value) => value || '—' },
          { title: t('status'), dataIndex: 'status', render: (value) => <Tag>{value}</Tag> },
          {
            title: t('actions'),
            render: (_, row: Item) => canManage
              ? <Button size="small" onClick={() => navigate(`/education/questions/items/${row.id}/edit`)}>{t('edit')}</Button>
              : null,
          },
        ]} />
      </Card>
    </EducationPageChrome>
  );
}
